<template>
  <div class="ele-body">
    <el-card shadow="never">
      <div class="exchange-container">
        <!-- 标题区域 -->
        <div class="header-section">
          <div class="header-icon">
            <i class="el-icon-switch-button"></i>
          </div>
          <h2>货币兑换</h2>
          <p>快速、安全的多币种兑换服务</p>
        </div>

        <div class="exchange-content">
          <!-- 左侧：兑换表单 -->
          <div class="form-section">
            <el-form ref="exchangeForm" :model="form" :rules="rules" label-width="90px">
              <el-form-item label="从币种" prop="fromCurrency">
                <el-select 
                  v-model="form.fromCurrency" 
                  placeholder="选择要兑换的币种"
                  @change="handleCurrencyChange"
                  class="currency-select">
                  <el-option
                    v-for="item in currencyList"
                    :key="item.code"
                    :value="item.code"
                    :label="`${item.name} (${item.code})`">
                    <div class="currency-option">
                      <img :src="item.icon" class="currency-icon-small" />
                      <span class="currency-name">{{ item.name }}</span>
                      <span class="currency-code">{{ item.code }}</span>
                    </div>
                  </el-option>
                </el-select>
              </el-form-item>

              <el-form-item label="兑换数量" prop="amount">
                <el-input-number 
                  v-model="form.amount" 
                  :min="0" 
                  :precision="8"
                  :max="999999"
                  placeholder="请输入兑换数量"
                  class="amount-input">
                </el-input-number>
                <div class="available-balance">
                  <span>可用余额：{{ availableBalance }} {{ form.fromCurrency }}</span>
                  <el-link type="primary" @click="useMaxBalance" size="small">全部使用</el-link>
                </div>
              </el-form-item>

              <el-form-item label="到币种" prop="toCurrency">
                <el-select 
                  v-model="form.toCurrency" 
                  placeholder="选择目标币种"
                  @change="handleCurrencyChange"
                  class="currency-select">
                  <el-option
                    v-for="item in currencyList"
                    :key="item.code"
                    :value="item.code"
                    :label="`${item.name} (${item.code})`"
                    :disabled="item.code === form.fromCurrency">
                    <div class="currency-option">
                      <img :src="item.icon" class="currency-icon-small" />
                      <span class="currency-name">{{ item.name }}</span>
                      <span class="currency-code">{{ item.code }}</span>
                    </div>
                  </el-option>
                </el-select>
              </el-form-item>

              <el-form-item>
                <div class="exchange-result">
                  <div class="result-label">将获得</div>
                  <div class="result-amount">
                    <span class="result-value">{{ calculateResult }}</span>
                    <span class="result-currency">{{ form.toCurrency }}</span>
                  </div>
                  <div class="result-rate">
                    汇率：1 {{ form.fromCurrency }} ≈ {{ exchangeRate }} {{ form.toCurrency }}
                  </div>
                </div>
              </el-form-item>

              <el-form-item>
                <el-button 
                  type="primary" 
                  @click="handleExchange" 
                  :loading="loading"
                  class="exchange-button"
                  :disabled="!canExchange">
                  <i class="el-icon-switch-button"></i>
                  立即兑换
                </el-button>
              </el-form-item>
            </el-form>
          </div>

          <!-- 右侧：币种信息和历史记录 -->
          <div class="info-section">
            <!-- 币种信息 -->
            <div class="currency-info-card">
              <div class="info-header">
                <i class="el-icon-info"></i>
                <span>币种信息</span>
              </div>
              <div v-if="currentCurrency" class="currency-details">
                <div class="detail-item">
                  <img :src="currentCurrency.icon" class="currency-icon-large" />
                  <div class="detail-content">
                    <div class="currency-title">{{ currentCurrency.name }}</div>
                    <div class="currency-subtitle">{{ currentCurrency.code }}</div>
                  </div>
                </div>
                <div class="detail-stats">
                  <div class="stat-item">
                    <div class="stat-label">24h涨跌</div>
                    <div class="stat-value" :class="currentCurrency.change24h >= 0 ? 'positive' : 'negative'">
                      {{ currentCurrency.change24h >= 0 ? '+' : '' }}{{ currentCurrency.change24h }}%
                    </div>
                  </div>
                  <div class="stat-item">
                    <div class="stat-label">市值排名</div>
                    <div class="stat-value">{{ currentCurrency.rank }}位</div>
                  </div>
                </div>
              </div>
            </div>

            <!-- 最近交易记录 -->
            <div class="history-card">
              <div class="info-header">
                <i class="el-icon-time"></i>
                <span>最近交易</span>
              </div>
              <div v-if="exchangeHistory.length === 0" class="empty-history">
                <i class="el-icon-document"></i>
                <p>暂无交易记录</p>
              </div>
              <div v-else class="history-list">
                <div 
                  v-for="(item, index) in exchangeHistory" 
                  :key="index" 
                  class="history-item">
                  <div class="history-main">
                    <div class="history-type">
                      <i class="el-icon-right"></i>
                      <span>{{ item.fromAmount }} {{ item.fromCurrency }}</span>
                      <span class="arrow">→</span>
                      <span>{{ item.toAmount }} {{ item.toCurrency }}</span>
                    </div>
                    <div class="history-time">{{ formatTime(item.time) }}</div>
                  </div>
                  <div class="history-status" :class="item.status">
                    {{ item.status === 'success' ? '已完成' : item.status === 'pending' ? '处理中' : '失败' }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </el-card>
  </div>
</template>

<script>
export default {
  name: 'Exchange',
  data() {
    return {
      form: {
        fromCurrency: 'USDT',
        toCurrency: 'BTC',
        amount: ''
      },
      rules: {
        fromCurrency: [
          { required: true, message: '请选择源币种', trigger: 'change' }
        ],
        amount: [
          { required: true, message: '请输入兑换数量', trigger: 'blur' },
          { 
            validator: (rule, value, callback) => {
              if (!value || value <= 0) {
                return callback(new Error('兑换数量必须大于0'));
              }
              callback();
            }, 
            trigger: 'blur' 
          }
        ],
        toCurrency: [
          { required: true, message: '请选择目标币种', trigger: 'change' }
        ]
      },
      loading: false,
      // 支持的币种列表（模拟数据）
      currencyList: [
        {
          code: 'BTC',
          name: '比特币',
          icon: 'https://cryptoicon-api.vercel.app/api/icon/btc',
          rank: 1,
          change24h: 2.35
        },
        {
          code: 'ETH',
          name: '以太坊',
          icon: 'https://cryptoicon-api.vercel.app/api/icon/eth',
          rank: 2,
          change24h: -1.23
        },
        {
          code: 'USDT',
          name: '泰达币',
          icon: 'https://cryptoicon-api.vercel.app/api/icon/usdt',
          rank: 3,
          change24h: 0.05
        },
        {
          code: 'BNB',
          name: '币安币',
          icon: 'https://cryptoicon-api.vercel.app/api/icon/bnb',
          rank: 4,
          change24h: 3.12
        },
        {
          code: 'SOL',
          name: 'Solana',
          icon: 'https://cryptoicon-api.vercel.app/api/icon/sol',
          rank: 5,
          change24h: 4.56
        },
        {
          code: 'ADA',
          name: '艾达币',
          icon: 'https://cryptoicon-api.vercel.app/api/icon/ada',
          rank: 6,
          change24h: -0.45
        },
        {
          code: 'DOT',
          name: '波卡币',
          icon: 'https://cryptoicon-api.vercel.app/api/icon/dot',
          rank: 7,
          change24h: 1.78
        },
        {
          code: 'MATIC',
          name: 'Polygon',
          icon: 'https://cryptoicon-api.vercel.app/api/icon/matic',
          rank: 8,
          change24h: -2.34
        }
      ],
      // 汇率数据（模拟数据）
      exchangeRates: {
        'BTC-USDT': 45000,
        'ETH-USDT': 2800,
        'USDT-BTC': 0.0000222,
        'USDT-ETH': 0.000357,
        'BTC-ETH': 16.07,
        'ETH-BTC': 0.062,
        'BNB-USDT': 320,
        'SOL-USDT': 95,
        'ADA-USDT': 0.45,
        'DOT-USDT': 6.5,
        'MATIC-USDT': 0.85,
        'USDT-BNB': 0.003125,
        'USDT-SOL': 0.0105,
        'USDT-ADA': 2.22,
        'USDT-DOT': 0.1538,
        'USDT-MATIC': 1.176
      },
      // 余额数据（模拟数据）
      balances: {
        'BTC': 0.5,
        'ETH': 10,
        'USDT': 5000,
        'BNB': 50,
        'SOL': 200,
        'ADA': 5000,
        'DOT': 500,
        'MATIC': 10000
      },
      // 交易历史（模拟数据）
      exchangeHistory: []
    };
  },
  computed: {
    // 当前选择的币种信息
    currentCurrency() {
      return this.currencyList.find(item => item.code === this.form.fromCurrency);
    },
    // 当前汇率
    exchangeRate() {
      const key = `${this.form.fromCurrency}-${this.form.toCurrency}`;
      return this.exchangeRates[key] || 1;
    },
    // 计算兑换结果
    calculateResult() {
      if (!this.form.amount || this.form.amount <= 0) {
        return '0.00000000';
      }
      const result = this.form.amount * this.exchangeRate;
      return result.toFixed(8).replace(/\.?0+$/, '');
    },
    // 可用余额
    availableBalance() {
      return this.balances[this.form.fromCurrency] || 0;
    },
    // 是否可以兑换
    canExchange() {
      return this.form.fromCurrency && 
             this.form.toCurrency && 
             this.form.fromCurrency !== this.form.toCurrency &&
             this.form.amount > 0 &&
             this.form.amount <= this.availableBalance;
    }
  },
  mounted() {
    // 初始化时加载汇率
    this.loadExchangeRates();
    // 加载交易历史
    this.loadExchangeHistory();
  },
  methods: {
    // 加载汇率（模拟API调用）
    loadExchangeRates() {
      // 模拟异步加载
      setTimeout(() => {
        this.$message.success('汇率数据已更新');
      }, 500);
    },
    // 币种变化处理
    handleCurrencyChange() {
      if (this.form.fromCurrency === this.form.toCurrency) {
        this.$message.warning('源币种和目标币种不能相同');
        this.form.toCurrency = '';
      }
    },
    // 使用全部余额
    useMaxBalance() {
      this.form.amount = this.availableBalance;
    },
    // 执行兑换
    handleExchange() {
      this.$refs.exchangeForm.validate((valid) => {
        if (!valid) {
          return false;
        }
        
        if (this.form.amount > this.availableBalance) {
          this.$message.error('余额不足');
          return;
        }

        if (this.form.fromCurrency === this.form.toCurrency) {
          this.$message.warning('源币种和目标币种不能相同');
          return;
        }

        this.loading = true;
        
        // 模拟API调用
        this.$http.post('/index/exchange', {
          fromCurrency: this.form.fromCurrency,
          toCurrency: this.form.toCurrency,
          amount: this.form.amount
        }).then(res => {
          this.loading = false;
          if (res.data.code === 0) {
            this.$message.success('兑换成功！');
            
            // 更新余额
            this.balances[this.form.fromCurrency] -= this.form.amount;
            this.balances[this.form.toCurrency] = (this.balances[this.form.toCurrency] || 0) + 
              parseFloat(this.calculateResult);
            
            // 添加交易记录
            this.addExchangeHistory({
              fromCurrency: this.form.fromCurrency,
              toCurrency: this.form.toCurrency,
              fromAmount: this.form.amount,
              toAmount: this.calculateResult,
              status: 'success',
              time: new Date()
            });
            
            // 重置表单
            this.form.amount = '';
            
            // 刷新列表
            this.loadExchangeHistory();
          } else {
            // 使用弹窗显示错误信息
            this.$alert(res.data.msg || '兑换失败', '提示', {
              confirmButtonText: '确定',
              type: 'error',
              callback: () => {}
            });
          }
        }).catch(e => {
          this.loading = false;
          console.error('兑换接口调用失败，这是模拟数据', e);
          
          // 模拟成功场景
          this.$message.success('兑换成功！（模拟数据）');
          
          // 更新余额
          this.balances[this.form.fromCurrency] -= this.form.amount;
          this.balances[this.form.toCurrency] = (this.balances[this.form.toCurrency] || 0) + 
            parseFloat(this.calculateResult);
          
          // 添加交易记录
          this.addExchangeHistory({
            fromCurrency: this.form.fromCurrency,
            toCurrency: this.form.toCurrency,
            fromAmount: this.form.amount,
            toAmount: this.calculateResult,
            status: 'success',
            time: new Date()
          });
          
          // 重置表单
          this.form.amount = '';
          
          // 刷新列表
          this.loadExchangeHistory();
        });
      });
    },
    // 加载交易历史
    loadExchangeHistory() {
      // 模拟API调用
      setTimeout(() => {
        // 可以从本地存储加载历史记录
        const history = localStorage.getItem('exchangeHistory');
        if (history) {
          this.exchangeHistory = JSON.parse(history);
        }
      }, 100);
    },
    // 添加交易历史
    addExchangeHistory(record) {
      this.exchangeHistory.unshift(record);
      if (this.exchangeHistory.length > 10) {
        this.exchangeHistory = this.exchangeHistory.slice(0, 10);
      }
      localStorage.setItem('exchangeHistory', JSON.stringify(this.exchangeHistory));
    },
    // 格式化时间
    formatTime(time) {
      const now = new Date(time);
      const month = String(now.getMonth() + 1).padStart(2, '0');
      const day = String(now.getDate()).padStart(2, '0');
      const hour = String(now.getHours()).padStart(2, '0');
      const minute = String(now.getMinutes()).padStart(2, '0');
      return `${month}-${day} ${hour}:${minute}`;
    }
  }
};
</script>

<style scoped>
::v-deep .el-card {
  border-radius: 4px;
}

.exchange-container {
  padding: 20px;
}

/* 标题区域 */
.header-section {
  text-align: center;
  margin-bottom: 30px;
}

.header-icon {
  width: 80px;
  height: 80px;
  margin: 0 auto 20px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 8px rgba(102, 126, 234, 0.25);
}

.header-icon i {
  font-size: 40px;
  color: white;
}

.header-section h2 {
  font-size: 28px;
  font-weight: 600;
  color: #303133;
  margin: 10px 0;
}

.header-section p {
  font-size: 14px;
  color: #909399;
  margin: 0;
}

/* 兑换卡片 */
.exchange-container {
  padding: 20px;
}

.exchange-content {
  display: flex;
  gap: 30px;
}

/* 表单区域 */
.form-section {
  flex: 1;
  background: #fafbfc;
  padding: 30px;
  border-radius: 16px;
}

.form-section .el-form-item {
  margin-bottom: 25px;
}

.currency-select {
  width: 100%;
}

.currency-option {
  display: flex;
  align-items: center;
  gap: 10px;
}

.currency-icon-small {
  width: 24px;
  height: 24px;
  border-radius: 50%;
}

.currency-name {
  flex: 1;
  font-weight: 500;
}

.currency-code {
  color: #909399;
  font-size: 12px;
}

.amount-input {
  width: 100%;
}

.available-balance {
  margin-top: 10px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 14px;
  color: #909399;
}

.exchange-result {
  background: linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%);
  padding: 25px;
  border-radius: 12px;
  text-align: center;
}

.result-label {
  font-size: 14px;
  color: #909399;
  margin-bottom: 10px;
}

.result-amount {
  font-size: 32px;
  font-weight: 700;
  color: #667eea;
  margin-bottom: 10px;
}

.result-value {
  margin-right: 8px;
}

.result-currency {
  font-size: 20px;
  color: #909399;
  font-weight: 500;
}

.result-rate {
  font-size: 14px;
  color: #909399;
}

.exchange-button {
  width: 100%;
  padding: 14px;
  font-size: 16px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border: none;
  border-radius: 12px;
  box-shadow: 0 6px 20px rgba(102, 126, 234, 0.4);
  transition: all 0.3s ease;
}

.exchange-button:hover {
  transform: translateY(-3px);
  box-shadow: 0 10px 30px rgba(102, 126, 234, 0.6);
}

.exchange-button:disabled {
  background: #dcdfe6;
  box-shadow: none;
  cursor: not-allowed;
}

/* 信息区域 */
.info-section {
  width: 400px;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.currency-info-card,
.history-card {
  background: #fafbfc;
  padding: 20px;
  border-radius: 16px;
}

.info-header {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 16px;
  font-weight: 600;
  color: #303133;
  margin-bottom: 20px;
}

.currency-details {
  padding-top: 10px;
}

.detail-item {
  display: flex;
  align-items: center;
  gap: 15px;
  margin-bottom: 20px;
}

.currency-icon-large {
  width: 48px;
  height: 48px;
  border-radius: 50%;
}

.detail-content {
  flex: 1;
}

.currency-title {
  font-size: 18px;
  font-weight: 600;
  color: #303133;
}

.currency-subtitle {
  font-size: 14px;
  color: #909399;
}

.detail-stats {
  display: flex;
  gap: 20px;
}

.stat-item {
  flex: 1;
}

.stat-label {
  font-size: 12px;
  color: #909399;
  margin-bottom: 5px;
}

.stat-value {
  font-size: 18px;
  font-weight: 700;
  color: #303133;
}

.stat-value.positive {
  color: #67c23a;
}

.stat-value.negative {
  color: #f56c6c;
}

/* 历史记录 */
.empty-history {
  text-align: center;
  padding: 40px 0;
  color: #909399;
}

.empty-history i {
  font-size: 48px;
  margin-bottom: 10px;
}

.history-list {
  max-height: 400px;
  overflow-y: auto;
}

.history-item {
  padding: 15px;
  background: white;
  border-radius: 4px;
  margin-bottom: 10px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.history-main {
  flex: 1;
}

.history-type {
  font-size: 14px;
  color: #303133;
  margin-bottom: 5px;
}

.history-type .arrow {
  margin: 0 8px;
  color: #909399;
}

.history-time {
  font-size: 12px;
  color: #909399;
}

.history-status {
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
}

.history-status.success {
  background: #f0f9ff;
  color: #67c23a;
}

.history-status.pending {
  background: #fff7e6;
  color: #faad14;
}

.history-status.failed {
  background: #fff1f0;
  color: #f56c6c;
}

/* 美化下拉框 */
::v-deep .el-input__inner,
::v-deep .el-input-number .el-input__inner {
  border-radius: 4px;
  font-size: 15px;
}

::v-deep .el-input__inner:focus,
::v-deep .el-input-number.is-focus .el-input__inner {
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.15);
}

::v-deep .el-select-dropdown__item.selected {
  color: #667eea;
  font-weight: 600;
}

/* 响应式 */
@media (max-width: 968px) {
  .exchange-content {
    flex-direction: column;
  }
  
  .info-section {
    width: 100%;
  }
  
  .header-icon {
    width: 60px;
    height: 60px;
  }
  
  .header-icon i {
    font-size: 30px;
  }
}
</style>
